import { __ } from '@wordpress/i18n';
import { FormTokenField } from '@wordpress/components';
import { useState, useMemo } from '@wordpress/element';
import { useSelect } from '@wordpress/data';
import { store as coreStore } from '@wordpress/core-data';

/**
 * User picker for the "specific users" source. Searches users by name and
 * stores the selected user IDs. Tokens display names; the component keeps a
 * name→id map (from search results + the resolved selection) to translate the
 * FormTokenField's string tokens back into IDs.
 */
export default function SpecificUsersControl({ userIds = [], onChange }) {
  const [search, setSearch] = useState('');

  const { suggestions, selected } = useSelect(
    (select) => {
      const core = select(coreStore);
      const args = { per_page: 10, orderby: 'name', order: 'asc' };
      if (search) {
        args.search = search;
      }
      return {
        suggestions: core.getUsers(args) || [],
        selected: userIds.length
          ? core.getUsers({ include: userIds, per_page: userIds.length }) || []
          : [],
      };
    },
    [search, JSON.stringify(userIds)]
  );

  const nameToId = useMemo(() => {
    const map = {};
    [...suggestions, ...selected].forEach((u) => {
      map[u.name] = u.id;
    });
    return map;
  }, [suggestions, selected]);

  const idToName = useMemo(() => {
    const map = {};
    selected.forEach((u) => {
      map[u.id] = u.name;
    });
    return map;
  }, [selected]);

  const value = userIds.map((id) => idToName[id] || String(id));
  const suggestionLabels = suggestions.map((u) => u.name);

  const handleChange = (tokens) => {
    const ids = tokens
      .map((token) => {
        if (typeof token === 'number') return token;
        if (nameToId[token] != null) return nameToId[token];
        // Tokens may still show as raw id strings before names resolve —
        // keep those that correspond to an already-selected id so editing
        // during the load race doesn't silently drop selections.
        const asNum = Number(token);
        return Number.isInteger(asNum) && userIds.includes(asNum) ? asNum : null;
      })
      .filter((id) => typeof id === 'number');
    onChange([...new Set(ids)].slice(0, 50));
  };

  return (
    <FormTokenField
      label={__('Users', 'brandy-blocks')}
      value={value}
      suggestions={suggestionLabels}
      onInputChange={setSearch}
      onChange={handleChange}
      maxLength={50}
      __experimentalExpandOnFocus
      __experimentalShowHowTo={false}
    />
  );
}
